import { useEffect, useRef, useState } from "react";

interface UseInfiniteScrollParams {
  fetchData: (page: number) => void;
  hasMore: boolean;
  isLoading: boolean;
  threshold?: number;
  debounceTime?: number;
  initialPage?: number;
}

interface UseInfiniteScrollResult {
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  page: number;
}

const useInfiniteScroll = ({
  fetchData,
  hasMore,
  isLoading,
  threshold = 100,
  debounceTime = 200,
  initialPage,
}: UseInfiniteScrollParams): UseInfiniteScrollResult => {
  const initialPageValue = initialPage !== undefined ? initialPage : 0;
  const [page, setPage] = useState(initialPageValue);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const fetchDataRef = useRef(fetchData);
  const skipFetchDataResetOnMountRef = useRef(true);

  const loadMore = () => {
    if (!isLoading && hasMore) {
      setPage((prevPage) => prevPage + 1);
    }
  };

  const debounce = (func: (...args: any[]) => void, wait: number) => {
    let timeout: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  };

  const handleScroll = debounce(() => {
    if (scrollContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } =
        scrollContainerRef.current;
      if (scrollTop + clientHeight >= scrollHeight - threshold) {
        loadMore();
      }
    }
  }, debounceTime);

  useEffect(() => {
    const currentRef = scrollContainerRef.current;
    if (currentRef) {
      currentRef.addEventListener("scroll", handleScroll);
    }
    return () => {
      if (currentRef) {
        currentRef.removeEventListener("scroll", handleScroll);
      }
    };
  }, [handleScroll]);

  useEffect(() => {
    fetchDataRef.current = fetchData;
  }, [fetchData]);

  useEffect(() => {
    fetchDataRef.current(page);
  }, [page]);

  useEffect(() => {
    if (skipFetchDataResetOnMountRef.current) {
      skipFetchDataResetOnMountRef.current = false;
      return;
    }

    setPage((prevPage) => {
      if (prevPage === initialPageValue) {
        fetchDataRef.current(initialPageValue);
        return prevPage;
      }

      return initialPageValue;
    });
  }, [fetchData, initialPageValue]);

  return { scrollContainerRef, page };
};

export default useInfiniteScroll;
